

















import Vue from 'vue';
import {
    defineComponent,
    computed,
    ref,
    watch,
} from '@vue/composition-api';

import SdsPageHeader from '@shared/components/pageHeader/SdsPageHeader.vue';
import { PageHeaderActionItem } from '@shared/components/pageHeader/types';
import { SportOfferStore } from '@store/modules/ui/sportOffer/sportPage/sportPageIndex';
import {
    useVueRouter,
    useVuexStore,
    isMobileLayout,
    useI18n,
} from '@superbet-group/web.lib.vue-utilities';

import { logger } from '@superbet-group/web.lib.utilities';

import { StreamSection } from '@app/store/modules/ui/sportOffer/sportPage/enums';
import { ClickEventType } from '@src/app/utils/clickAnalytics';
import { useBreadcrumbs } from './useBreadcrumbs';
import { getEventId } from './utils';
import { statsService } from '@src/federations/sportOffer/competitions/services/StatsService';
import { StatsEvent } from '@core/models/base/StatsEvent';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { ClickName } from '@superbet-group/product.analytics';
import EventDetailsPageHeader from './components/header/EventDetailsPageHeader.vue';
const videoIcon = 'toggle-video';
const videoTranslateKey = 'stats.match.actions.livestream';

const iconsMap = {
    [StreamSection.Visualization]: {
        id: StreamSection.Visualization,
        icon: 'toggle-soccer-field',
        translateKey: 'stats.match.actions.visualisation',
    },
    [StreamSection.Video]: {
        id: StreamSection.Video,
        // eslint-disable-next-line sonarjs/no-duplicate-string
        icon: videoIcon,
        translateKey: videoTranslateKey,
    },
    [StreamSection.Twitch]: {
        id: StreamSection.Twitch,
        icon: videoIcon,
        translateKey: videoTranslateKey,
    },
    [StreamSection.Youtube]: {
        id: StreamSection.Youtube,
        icon: videoIcon,
        translateKey: videoTranslateKey,
    },
    [StreamSection.Perform]: {
        id: StreamSection.Perform,
        icon: videoIcon,
        translateKey: videoTranslateKey,
    },
    [StreamSection.BetGenius]: {
        id: StreamSection.BetGenius,
        icon: videoIcon,
        translateKey: videoTranslateKey,
    },
    [StreamSection.Img]: {
        id: StreamSection.Img,
        icon: videoIcon,
        translateKey: videoTranslateKey,
    },
    [StreamSection.Infrontsports]: {
        id: StreamSection.Infrontsports,
        icon: videoIcon,
        translateKey: videoTranslateKey,
    },
    [StreamSection.Beter]: {
        id: StreamSection.Beter,
        icon: videoIcon,
        translateKey: videoTranslateKey,
    },
};

export default defineComponent({
    name: 'SdsEventDetailsPageHeader',
    setup() {
        const {
            route,
        } = useVueRouter();

        const statsEvent = ref<StatsEvent | null>();

        const {
            breadcrumbs,
            isStatsEventInQuery,
        } = useBreadcrumbs(statsEvent);

        const sportOfferUIStore = useVuexStore<SportOfferStore>('ui/sportOffer');

        const expandedEventData = sportOfferUIStore.getter('expandedEventData');
        const selectedStreamSection = sportOfferUIStore?.getter?.('selectedStreamSection');
        const availableStreamSections = sportOfferUIStore?.getter?.('availableStreamSections');
        const toggleSelectedStreamSection = sportOfferUIStore?.action?.('toggleSelectedStreamSection');
        const prepareStream = sportOfferUIStore?.action?.('prepareStream');
        const hasResolvedLivestreamOpening = ref(false);

        const name = computed(() => {
            const competitorOne = expandedEventData.value?.team1Name ?? statsEvent.value?.team1?.name;
            const competitorTwo = expandedEventData.value?.team2Name ?? statsEvent.value?.team2?.name;

            if (competitorOne?.length && competitorTwo?.length) {
                return `${competitorOne} - ${competitorTwo}`;
            }

            return '';
        });

        const handleStreamSelect = (stream: StreamSection | null) => {
            const clickName = selectedStreamSection?.value === stream || !stream
                ? ClickEventType.multimediaCloseClick
                : ClickEventType.multimediaStartClick;
            const multimediaClickAction = selectedStreamSection?.value === stream || !stream
                ? ClickName.MULTIMEDIA_CLOSE_CLICK : ClickName.MULTIMEDIA_START_CLICK;

            // @TODO(corneliu-iancu): Temporary dual analytics implementation.
            // We are currently in the process of migrating from $superAnalytics to AnalyticsSDK.
            // Both libraries are being used in parallel to validate data consistency between them.
            // DO NOT modify or remove calls to $superAnalytics until the migration is fully validated and approved.
            // Once validation is complete, $superAnalytics will be deprecated and removed.
            AnalyticsSDK.getInstance().log('click', {
                click_name: multimediaClickAction,
                click: { type: 'general_click' },
            });
            Vue.prototype.$superAnalytics.sendEvent(
                {
                    eventType: 'click',
                    click: {
                        clickName,
                        clickType: 'general_click',
                    },
                    AnalyticsEvent: 'click',
                },
            );

            toggleSelectedStreamSection?.(stream);

            prepareStream?.({ match: expandedEventData?.value, streamSection: stream });
        };

        const { translate } = useI18n();

        const actionItems = computed<PageHeaderActionItem[]>(() => {
            return expandedEventData.value?.isOngoing()
                ? availableStreamSections?.value?.map((stream: StreamSection) => ({
                    icon: iconsMap[stream]?.icon,
                    onClick: () => handleStreamSelect(iconsMap[stream]?.id),
                    selected: selectedStreamSection?.value === iconsMap[stream]?.id,
                    label: translate(iconsMap[stream]?.translateKey),
                } as PageHeaderActionItem))
                : [];
        });

        const shouldShowSdsPageHeader = computed(() => !isMobileLayout());

        const statsEventId = computed(() => statsEvent.value?.id);

        watch(() => route.value.params.eventSlug, async () => {
            const routeEventId = getEventId();

            if (isStatsEventInQuery.value && routeEventId && parseInt(routeEventId, 10) !== statsEventId.value) {
                try {
                    statsEvent.value = await statsService.getStatsEventResult(parseInt(routeEventId, 10));
                } catch (e) {
                    logger.error(e as Error);
                }
            }
        }, { immediate: true });

        watch(actionItems, (newItems) => {
            if (hasResolvedLivestreamOpening.value) { return; }

            const livestreamParam = route.value.query.ls;
            if (livestreamParam !== 'true') {
                hasResolvedLivestreamOpening.value = true;
                return;
            }

            const livestreamItem = newItems.find((i) => i.icon === videoIcon);
            if (livestreamItem) {
                hasResolvedLivestreamOpening.value = true;
                livestreamItem.onClick();
                setTimeout(() => {
                    const playButton = document.getElementsByClassName('btn--video')[0]as HTMLElement;
                    playButton?.click();
                }, 1000);
            }
        });

        return {
            name,
            breadcrumbs,
            actionItems,
            shouldShowSdsPageHeader,
            isMobileLayout: isMobileLayout(),

        };
    },
    components: {
        SdsPageHeader,
        EventDetailsPageHeader,
    },
});
